import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BookOpen,
  ChefHat,
  Compass,
  Dumbbell,
  Film,
  Goal,
  MapPin,
  Route,
  Trophy,
  Users,
} from "lucide-react";
import { PageHero, Reveal, SectionHeading } from "@/components/motion";

const COLLEGES = [
  { name: "Tuskegee University", city: "Tuskegee, AL", div: "NCAA DII", miles: 2, tags: ["DII", "HBCU"] },
  { name: "Auburn University", city: "Auburn, AL", div: "NCAA DI", miles: 25, tags: ["DI"] },
  { name: "Southern Union State CC", city: "Opelika, AL", div: "NJCAA", miles: 35, tags: ["JUCO"] },
  { name: "Alabama State University", city: "Montgomery, AL", div: "NCAA DI", miles: 40, tags: ["DI", "HBCU"] },
  { name: "Auburn University at Montgomery", city: "Montgomery, AL", div: "NCAA DII", miles: 40, tags: ["DII"] },
  { name: "Faulkner University", city: "Montgomery, AL", div: "NAIA", miles: 42, tags: ["NAIA"] },
  { name: "Huntingdon College", city: "Montgomery, AL", div: "NCAA DIII", miles: 42, tags: ["DIII"] },
  { name: "Central Alabama CC", city: "Alexander City, AL", div: "NJCAA", miles: 45, tags: ["JUCO"] },
  { name: "Troy University", city: "Troy, AL", div: "NCAA DI", miles: 50, tags: ["DI"] },
  { name: "Chattahoochee Valley CC", city: "Phenix City, AL", div: "NJCAA", miles: 60, tags: ["JUCO"] },
  { name: "Columbus State University", city: "Columbus, GA", div: "NCAA DII", miles: 65, tags: ["DII"] },
  { name: "LaGrange College", city: "LaGrange, GA", div: "NCAA DIII", miles: 75, tags: ["DIII"] },
  { name: "Point University", city: "West Point, GA", div: "NAIA", miles: 75, tags: ["NAIA"] },
  { name: "Marion Military Institute", city: "Marion, AL", div: "NJCAA", miles: 80, tags: ["JUCO"] },
  { name: "Fort Valley State University", city: "Fort Valley, GA", div: "NCAA DII", miles: 90, tags: ["DII", "HBCU"] },
];

const FILTERS = ["ALL", "DI", "DII", "DIII", "NAIA", "JUCO", "HBCU"];

const BENEFITS = [
  {
    icon: Dumbbell,
    title: "Physical Development",
    text: "A full year of strength, speed and skill work while your body matures into a college body. One high school offseason can't do what twelve structured months can.",
  },
  {
    icon: BookOpen,
    title: "Academic Preparation",
    text: "Repair your GPA, attack your test scores and build the study habits that make you admissible — and keep you eligible once you're on campus.",
  },
  {
    icon: Film,
    title: "Exposure & New Film",
    text: "A brand-new season of film that college coaches actually scout. Your proof on tape — not just your potential in a coach's notes.",
  },
  {
    icon: Users,
    title: "Character & Maturity",
    text: "Living on a schedule, away from home, to a standard. The kind of growth that coaches, parents and employers all recognize immediately.",
  },
];

const FACILITY = [
  {
    icon: ChefHat,
    title: "Full-Service Kitchen",
    text: "Our facility features a full-service kitchen where players dine together — nutrition is treated as part of training, not an afterthought.",
  },
  {
    icon: Goal,
    title: "Outdoor Football Field",
    text: "A regulation outdoor football field for practices, games and year-round position work under real lights and real weather.",
  },
  {
    icon: Trophy,
    title: "Indoor Basketball Court",
    text: "A full indoor basketball court so our hoops players — men's and women's — train and compete in any season, day or night.",
  },
];

export default function AboutPage() {
  const [filter, setFilter] = useState("ALL");
  const shown = COLLEGES.filter((c) => filter === "ALL" || c.tags.includes(filter));

  return (
    <>
      <PageHero
        testid="about-hero"
        eyebrow="About the academy"
        lines={[<>Who we are.</>, <span className="text-ice">Where we're going.</span>]}
        sub="Alabama Bulldogs Sports Academy is a post-graduate sports academy located at 4280 US-29 in Tuskegee, Alabama. We exist for one reason: to give unsigned and overlooked athletes a real, structured path to a college roster."
      />

      {/* WHO WE ARE + WHAT POST-GRAD IS */}
      <section data-testid="postgrad-benefits-grid" className="border-t border-white/5 py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="What post-graduate sports really is"
            title={
              <>
                A gap year — <span className="text-ice">with structure.</span>
              </>
            }
          />
          <Reveal delay={0.15}>
            <div className="mt-8 max-w-4xl space-y-5 text-base leading-relaxed text-slate-300 sm:text-lg">
              <p>
                Post-graduate sports is one extra year between high school and college. Instead of jumping into a
                college program you aren't ready for yet — physically, academically or mentally — you spend the year
                at the academy: you lift, you practice, you play a full competitive schedule, and you take classes.
              </p>
              <p>
                For the right athlete, it is the difference between{" "}
                <span className="text-white">"we would have liked to see more"</span> and{" "}
                <span className="text-ice">"we have to get this kid on campus."</span> If you need to develop
                physically, you get a year in a real program. If you need to develop academically, you get a year to
                fix your transcript and your test scores. And if the talent was always there but nobody saw it, you
                get a full season of new film with coaches in the gym.
              </p>
              <p>
                None of this is a detour. It's the road most unsigned players simply never get shown — and it's the
                entire reason this academy exists in Tuskegee.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.08} className="h-full">
                <div className="group h-full rounded-2xl border border-white/10 bg-navy-800 p-6 transition-colors hover:border-ice/50">
                  <div className="grid h-12 w-12 place-items-center rounded-xl border border-ice/30 bg-ice/10 text-ice transition-colors group-hover:bg-ice group-hover:text-navy-900">
                    <b.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-extrabold uppercase tracking-wide text-white">
                    {b.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-300">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LOCATION + COLLEGE RADAR */}
      <section className="border-t border-white/5 bg-navy-850 py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid items-end gap-8 lg:grid-cols-[1.2fr_0.8fr]">
            <SectionHeading
              eyebrow="Built in college country"
              title={
                <>
                  Minutes from <span className="text-ice">Tuskegee University.</span>
                </>
              }
              description="Our facility sits in Tuskegee — home of Tuskegee University and its storied Golden Tigers. Our athletes live and train in a true college town, surrounded by college sports and the everyday standard of the next level."
            />
            <Reveal delay={0.15}>
              <div
                data-testid="location-card"
                className="glass flex items-start gap-3 rounded-xl p-5"
              >
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-crimson" />
                <div>
                  <p className="font-display text-lg font-extrabold uppercase text-white">Find us in Tuskegee</p>
                  <p className="mt-1 text-sm text-slate-300">4280 US-29, Tuskegee, Alabama</p>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="mt-14">
            <p data-testid="colleges-radar-list-eyebrow" className="eyebrow mb-6">
              Colleges & universities within a 100-mile radius
            </p>
            <div className="flex flex-wrap gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  data-testid={`college-filter-${f.toLowerCase()}`}
                  onClick={() => setFilter(f)}
                  className={`rounded-full border px-4 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors ${
                    filter === f
                      ? "border-ice bg-ice/10 text-ice"
                      : "border-white/15 text-silver-dim hover:border-ice/50 hover:text-white"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
            <motion.ul
              key={filter}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 overflow-hidden rounded-2xl border border-white/10"
            >
              {shown.map((c, i) => (
                <li
                  key={c.name}
                  data-testid={`college-row-${i}`}
                  className={`flex flex-wrap items-center justify-between gap-3 bg-navy-800/80 px-5 py-4 transition-colors hover:bg-navy-600 ${
                    i !== 0 ? "border-t border-white/5" : ""
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-xs text-slate-500">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <p className="font-display text-lg font-bold uppercase tracking-wide text-white">{c.name}</p>
                      <p className="text-xs text-slate-400">{c.city}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {[...c.tags].sort().map((t) => (
                      <span
                        key={t}
                        className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] ${
                          t === "HBCU" ? "bg-crimson/15 text-crimson-bright" : "bg-ice/10 text-ice"
                        }`}
                      >
                        {t}
                      </span>
                    ))}
                    <span className="font-mono text-xs text-slate-400">~{c.miles} mi</span>
                  </div>
                </li>
              ))}
            </motion.ul>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500">
              Distances are approximate driving miles from 4280 US-29, Tuskegee, AL.
            </p>
          </Reveal>
        </div>
      </section>

      {/* RECRUITING + NIL GUIDANCE */}
      <section className="border-t border-white/5 py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="We coach the process too"
            title={
              <>
                Recruiting & NIL — <span className="text-ice">decoded.</span>
              </>
            }
            description="Talent gets you considered. Knowing the process gets you recruited. Every ASA player and family gets guided through both."
          />
          <div className="mt-14 grid gap-6 lg:grid-cols-2">
            <Reveal className="h-full">
              <div
                data-testid="recruiting-guidance-card"
                className="group h-full rounded-2xl border border-white/10 bg-navy-800 p-8 transition-colors hover:border-ice/50"
              >
                <Route className="h-8 w-8 text-ice" />
                <h3 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-wide text-white">
                  The recruiting process, decoded
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-300 sm:text-base">
                  We walk every player and every family through how college recruiting actually works: eligibility
                  rules, contact windows, official visits, offer sheets and signing day. You'll always know what
                  happens next, who's calling, and what it means — so nothing catches your family by surprise.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12} className="h-full">
              <div
                data-testid="nil-guidance-card"
                className="group h-full rounded-2xl border border-white/10 bg-navy-800 p-8 transition-colors hover:border-crimson/50"
              >
                <Compass className="h-8 w-8 text-crimson-bright" />
                <h3 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-wide text-white">
                  The NIL space, navigated
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-300 sm:text-base">
                  Name, Image and Likeness changed college sports — and most families have never been walked through
                  it. We help players and parents understand what NIL is, what questions to ask, and how to handle
                  opportunities the right way without losing focus or eligibility.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FACILITY */}
      <section data-testid="facility-section" className="border-t border-white/5 bg-navy-850 py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow="The facility"
            title={
              <>
                Train, eat, sleep — <span className="text-ice">repeat.</span>
              </>
            }
            description="Everything a post-graduate year needs, on one campus in Tuskegee."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {FACILITY.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.1} className="h-full">
                <div
                  data-testid={`facility-tour-${f.title.split(" ")[0].toLowerCase()}`}
                  className="group h-full rounded-2xl border border-white/10 bg-navy-800 p-8 transition-colors hover:border-ice/50"
                >
                  <div className="grid h-12 w-12 place-items-center rounded-xl border border-ice/30 bg-ice/10 text-ice transition-colors group-hover:bg-ice group-hover:text-navy-900">
                    <f.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-wide text-white">
                    {f.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-300">{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15} className="mt-16">
            <div className="clip-notch flex flex-col items-start justify-between gap-6 border border-ice/30 bg-ice/5 p-8 sm:flex-row sm:items-center">
              <div>
                <p className="font-display text-2xl font-extrabold uppercase text-white sm:text-3xl">
                  A year here changes the conversation.
                </p>
                <p className="mt-2 text-sm text-slate-300">Come see the academy for yourself.</p>
              </div>
              <Link to="/#apply" data-testid="about-apply-cta" className="btn-primary shrink-0">
                Apply Now <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
